import { createContext, Dispatch, ReactNode, useContext, useEffect, useMemo, useReducer } from 'react';
import {
  DamageSource,
  EMPTY_GUI_GAME_STATE,
  EntityUpgradesList,
  Generator,
  GUIActions,
  GUIGameState,
  UnitType,
  UpgradeActions,
  UpgradeType,
} from '../types/game-types';
import { ensureGameStateBootstrapped } from './init-state';
import { clickDragonReducer } from './reducers/click-dragon';
import { clickFarmReducer } from './reducers/click-farm';
import { tickReducer } from './reducers/tick';
import { saveState } from './save-state';
import { generateGeneratorsReducer } from './reducers/generate-generators';
import { generateGoldFromClickReducer } from './reducers/generate-gold-from-click';
// import { getGeneratorsByLevel } from './helpers/get-generators';
import { EventsManager } from '@game-libs/events-manager';
import { CONFIG } from '../config';
import { GameplayEvents } from '../types/game-events';
import { manageStunEffectReducer } from './reducers/manage-stun-effect';
import { manageInspirationReducer } from './reducers/manage-inspiration';
import { manageDungeonCooldownReducer } from './reducers/manage-dungeon-cooldown';
import { buildStatsSnapshot, updateStats } from '../stats';
import { managePoisonReducer } from './reducers/manage-poison';
import { useCurrencyStore } from './zustand/useCurrencyStore';
import { useDungeonCrawlerStore } from './zustand/useDungeonCrawlerStore';
import { useDragonHealthStore } from './zustand/useDragonHealthStore';
import { EXPECTED_SAVE_VERSION, INITIAL_TICK_LENGTH_MS, useGameStatusStore } from './zustand/useGameStatusStore';
import { useGameStatsStore } from './zustand/useGameStatsStore';
import { useTimedEventsStore } from './zustand/useTimedEventsStore';
import { useGeneratorsStore } from './zustand/useGeneratorsStore';
import { useUpgradesStore } from './zustand/useUpgradesStore';
import { sceneNameToGameLevelId } from '../types/game-levels';
import { TimedEventsOrchestrator } from '@game-libs/timed-events-orchestrator';
import { GlobalGameStore } from '@game-libs/global-game-store';
import { GameEvents, MenuEvents } from '@game-libs/models';
import { AudioManager } from '@game-libs/audio-manager';
import { QuickGameDialog } from '@game-libs/quick-game-dialog';
import { getI18n } from 'react-i18next';
import { AchievementsHandler } from '@game-libs/achievements-handler';

const timedEvents = TimedEventsOrchestrator();
const { setStatInt, setStatFloat, incrementStatInt } = AchievementsHandler();

/**
 * When true, skip the combat/economy tick (dummy level cleared, dragon dead, or post–coup de grâce).
 * Shared by `GUIActions.TICK` and `GUIActions.GENERATE_GENERATORS` so both stay in sync.
 */
function gameplayTickIsPaused(): boolean {
  const gameStatus = useGameStatusStore.getState();
  const currentLevelName = gameStatus.dragonName;

  if (currentLevelName === 'dummy' && gameStatus.dummyKilled) {
    return true;
  }

  if (
    currentLevelName !== 'dummy' &&
    (useDragonHealthStore.getState().dragonHealth <= 0 || gameStatus.playedCoupDeGrace)
  ) {
    return true;
  }

  return false;
}

const reducer = (gameState: GUIGameState, action: { type: GUIActions | UpgradeActions; payload?: any }) => {
  let state = gameState;
  let newState: GUIGameState;
  const { language } = getI18n();
  const { actions } = GlobalGameStore();
  const { emit } = EventsManager();
  const { playSFXNow } = AudioManager();

  switch (action.type) {
    case GUIActions.SET_GOLD:
      useCurrencyStore.getState().addGold(action.payload);
      newState = state;
      break;
    case GUIActions.HEAL_DRAGON: {
      const gameStatus = useGameStatusStore.getState();
      if (gameStatus.dummyKilled) {
        return gameState;
      }
      const dragonName = gameStatus.dragonName;
      const maxHealth = CONFIG.game.initialDragonHealth[dragonName as keyof typeof CONFIG.game.initialDragonHealth];
      const nextHealth = Math.min(useDragonHealthStore.getState().dragonHealth + action.payload, maxHealth);
      useDragonHealthStore.getState().setHealth(nextHealth);
      newState = state;
      break;
    }

    case GUIActions.MET_DRAGON: {
      useGameStatusStore.getState().sync({ metBigDragon: true, dragonName: 'bigDragon' });
      const bigDragonHealth = CONFIG.game.initialDragonHealth.bigDragon;
      // Single source of truth: Zustand (poison / damageLastTick on the store stay as-is).
      useDragonHealthStore.getState().setHealth(bigDragonHealth);
      newState = state;
      break;
    }
    // Per-frame store/timed-event work only (same string as world `emit(GUIActions.TICK)` but this is reducer dispatch).
    case GUIActions.TICK:
      if (gameplayTickIsPaused()) {
        return gameState;
      }

      // Per-level tick counter; `GENERATE_GENERATORS` / generator reducers read this value (already incremented).
      useGameStatsStore.getState().incrementActiveLevelTick();
      tickReducer(state);

      timedEvents.update();
      useTimedEventsStore.getState().setEvents([...timedEvents.allEvents]);

      // Bard inspiration timers + dungeon leave cooldown: Zustand only, no GUIGameState change.
      manageInspirationReducer(useUpgradesStore.getState().upgrades);
      manageDungeonCooldownReducer(state);

      return state;

    case GUIActions.GENERATE_GENERATORS: {
      // Must run in the same macrotask after `dispatch(TICK)` so tick count / timed events are up to date.
      if (gameplayTickIsPaused()) {
        return gameState;
      }

      const gameStatus = useGameStatusStore.getState();

      const goldBeforeTick = useCurrencyStore.getState().gold;
      const dragonHealthBeforeGenerators = useDragonHealthStore.getState().dragonHealth;

      newState = state;

      if (gameStatus.stunEffectTickDurationRemaining > 0) {
        emit(GameplayEvents.STUN_UNITS);
        newState = manageStunEffectReducer(newState);
      } else {
        generateGeneratorsReducer();
        useGeneratorsStore.getState().runUnlockTier();
      }

      const dragonHealthAfterGenerators = useDragonHealthStore.getState().dragonHealth;
      const damageThisTick = dragonHealthBeforeGenerators - dragonHealthAfterGenerators;

      const goldAfterTick = useCurrencyStore.getState().gold;
      const goldThisTick = goldAfterTick - goldBeforeTick;
      useCurrencyStore.getState().setGoldLastTick(goldThisTick);

      // Handle Poison damage and gold
      newState = managePoisonReducer(newState);

      // Tick DPS stats use generator damage only (poison is applied after this snapshot).
      useDragonHealthStore.getState().setDamageLastTick(damageThisTick);

      const inspirationForCritical = useCurrencyStore.getState().inspiration;
      if (damageThisTick > 0) {
        emit(GameplayEvents.DAMAGE_DRAGON, {
          damage: damageThisTick,
          gold: goldThisTick,
          source: DamageSource.Tick,
          isCritical: inspirationForCritical.remainingActive > 0,
        });
      }

      // To be caught by the game logic to generate units
      emit(GUIActions.TICK, {
        payload: useGeneratorsStore.getState().generators,
        upgrades: useUpgradesStore.getState().upgrades,
      });

      // Save every tick
      saveState(newState);
      break;
    }
    case GUIActions.ACTIVATE_INSPIRATION:
      // Active duration is controlled by the Bard's Obnoxious Guitarist upgrade.
      // Starts at 5 and grows by 1 per purchase.
      const obnoxiousGuitarist = useUpgradesStore.getState().upgrades.bard?.obnoxiousGuitarist;
      const activeDuration = obnoxiousGuitarist?.multiplier ?? 5;

      useCurrencyStore.getState().setInspiration({ remainingActive: activeDuration });
      newState = state;
      break;
    case GUIActions.CLICK_BIRD: {
      const clickUpgrades = useUpgradesStore.getState().upgrades;
      const currentGold = useCurrencyStore.getState().gold;

      const moneyToAward = Math.floor(
        Math.max(
          20,
          currentGold *
            0.1 *
            clickUpgrades.click.moneyCursor.multiplier *
            clickUpgrades.click.goldenExplosion.multiplier,
        ),
      );

      useCurrencyStore.getState().addGold(moneyToAward);
      useGameStatsStore.getState().incrementActiveLevelMoneyBids(1);
      newState = state;

      // setStatInt('birds_killed', useGameStatsStore.getState().getActiveMoneyBidsKilled());
      incrementStatInt('birds_killed', 1);

      emit(GUIActions.CLICK_BIRD, { moneyToAward, screenCoordinates: action.payload.screenCoordinates });

      break;
    }
    case GUIActions.CLICK_DRAGON:
      if (useDragonHealthStore.getState().dragonHealth <= 0 || useGameStatusStore.getState().dummyKilled) {
        return gameState;
      }

      const goldBeforeClick = useCurrencyStore.getState().gold;
      const dragonHealthBeforeClick = useDragonHealthStore.getState().dragonHealth;

      newState = clickDragonReducer(state);

      setStatInt('click_amount', useGameStatsStore.getState().getActiveNumOfClicks());

      const damageThisClick = dragonHealthBeforeClick - useDragonHealthStore.getState().dragonHealth;

      newState = generateGoldFromClickReducer(newState, damageThisClick);
      const goldThisClick = useCurrencyStore.getState().gold - goldBeforeClick;

      useGeneratorsStore.getState().runUnlockTier();

      const regularExpectedDamage =
        CONFIG.game.damagePerClick * useUpgradesStore.getState().upgrades.click.ironFinger.multiplier;

      const inspirationAfterClick = useCurrencyStore.getState().inspiration;
      const isCritical = damageThisClick > regularExpectedDamage || inspirationAfterClick.remainingActive > 0;

      emit(GameplayEvents.DAMAGE_DRAGON, {
        damage: damageThisClick,
        gold: goldThisClick,
        source: DamageSource.Click,
        isCritical,
      });

      emit(GUIActions.CLICK_DRAGON, { damage: damageThisClick });

      break;
    case GUIActions.CLICK_FARM:
      newState = clickFarmReducer(state);
      break;
    case GUIActions.ADD_STUN_EFFECT:
      newState = manageStunEffectReducer(state, true);
      break;
    case GUIActions.BUY_GENERATOR: {
      const purchased = useGeneratorsStore.getState().buyGenerator(action.payload);
      if (purchased) {
        emit(GUIActions.BUY_GENERATOR, action.payload);
      }
      newState = state;
      break;
    }
    case GUIActions.REMOVE_GENERATORS:
      useGeneratorsStore.getState().addOrRemoveGenerators(action.payload);
      newState = state;
      break;
    case GUIActions.ADD_GENERATORS:
      useGeneratorsStore.getState().addOrRemoveGenerators(action.payload);
      newState = state;
      break;
    case UpgradeActions.UNLOCK_UPGRADE: {
      const childrenToUnlock = action.payload.children as UpgradeType[];
      const entityType = action.payload.entityType;
      useUpgradesStore.getState().unlockUpgradeChildren(entityType, childrenToUnlock);
      newState = state;
      break;
    }
    case UpgradeActions.SHOW_UPGRADE: {
      const childrenToShow = action.payload.children as UpgradeType[];
      const entityType = action.payload.entityType;
      useUpgradesStore.getState().showUpgradeChildren(entityType, childrenToShow);
      newState = state;
      break;
    }
    case UpgradeActions.BUY_UPGRADE: {
      const { id, entityType } = action.payload;
      const upgrade = useUpgradesStore.getState().upgrades[entityType][id as UpgradeType];

      // Skip purchase if too expensive or purchase limit reached
      if (
        useCurrencyStore.getState().gold < (upgrade.cost.gold || 0) ||
        (upgrade.purchaseLimit && upgrade.purchased >= upgrade.purchaseLimit)
      ) {
        return gameState;
      }

      const bought = useUpgradesStore.getState().buyUpgrade(id, entityType);
      if (!bought) {
        return gameState;
      }

      const multiplierAfter = useUpgradesStore.getState().upgrades[entityType][id as UpgradeType].multiplier;

      emit(GameplayEvents.PURCHASED_UPGRADE, {
        id,
        multiplier: multiplierAfter,
      });
      emit(UpgradeActions.BUY_UPGRADE, action.payload);

      if (id === UpgradeType.ManaSurge) {
        useCurrencyStore.getState().setMana(useUpgradesStore.getState().upgrades.wizard.manaPool.multiplier);
        emit(GUIActions.ENOUGH_MANA);
      }

      newState = state;
      break;
    }
    case GUIActions.RESET_STATE:
      useCurrencyStore.getState().reset();
      useDragonHealthStore.getState().reset();
      useGeneratorsStore.getState().reset();
      useUpgradesStore.getState().reset();
      // Align store-only settings with a fresh reducer snapshot (do not full reset game progress flags).
      useGameStatusStore.getState().sync({
        buyMax: false,
        showUI: true,
        tickLength: INITIAL_TICK_LENGTH_MS,
        saveVersion: EXPECTED_SAVE_VERSION,
      });
      useGameStatsStore.getState().resetSessionPlayStats();
      timedEvents.clearEvents();
      useTimedEventsStore.getState().reset();
      newState = { ...EMPTY_GUI_GAME_STATE };
      break;
    case GUIActions.INIT_GAME:
      // Set the state to the loaded state
      const loadedGameState = action.payload as GUIGameState;
      state = loadedGameState;

      const currentSceneName = GlobalGameStore().state.currentLevel.value;
      useGameStatsStore
        .getState()
        .setActiveLevelId(
          sceneNameToGameLevelId(currentSceneName && currentSceneName.length > 0 ? currentSceneName : 'tutorial'),
        );

      const { openDialog } = QuickGameDialog({
        pathToInkFiles: './dialogs',
        pathToSpeakerFiles: './dialogs/pictures',
        containerId: 'ui-wrapper',
      });

      const persistedTimedEvents = useTimedEventsStore.getState().events;
      if (persistedTimedEvents.length === 0) {
        timedEvents.addEvents([
          {
            id: 'roar',
            iconId: 'roar',
            categoryId: 'roar',
            emitId: GameplayEvents.DRAGON_ROAR,
            group: 'DragonAttack',
            pauseGroupOnActivation: true,
            duration: 7.7 * 60,
            elapsed: 2 * 60, // Starts at 2 minutes already passed
            once: false,
          },
          // {
          //   id: 'fire',
          //   categoryId: 'fire',
          //   duration: 100,
          //   group: 'DragonAttack',
          //   pauseGroupOnActivation: false,
          //   elapsed: 0,
          //   once: false,
          // },
          {
            id: 'dungeon_keys',
            iconId: 'key',
            categoryId: 'dialog',
            emitId: 'dungeon_keys',
            pauseGroupOnActivation: true,
            group: 'dialog',
            duration: 10 * 60,
            elapsed: 0,
            once: true,
          },
          {
            id: 'invasion_start',
            iconId: 'damage',
            categoryId: 'dialog',
            emitId: 'invasion_start',
            pauseGroupOnActivation: true,
            group: 'dialog',
            duration: 30 * 60,
            elapsed: 0,
            once: true,
          },
          {
            id: 'pope_visit',
            iconId: 'cross',
            categoryId: 'dialog',
            emitId: 'pope_visit',
            group: 'dialog',
            pauseGroupOnActivation: true,
            duration: 38 * 60,
            elapsed: 0,
            once: true,
          },
          {
            id: 'catapult',
            iconId: 'catapult',
            categoryId: 'dialog',
            emitId: 'catapult',
            group: 'dialog',
            pauseGroupOnActivation: true,
            duration: 21 * 60,
            elapsed: 0,
            once: true,
          },
        ]);
      } else {
        timedEvents.loadEvents(persistedTimedEvents);

        // Fix bug where the dialog group is not unpaused if i refresh the game while a dialog is playing
        timedEvents.unpauseAll();
      }

      useTimedEventsStore.getState().setEvents([...timedEvents.allEvents]);

      // Update stats to ensure the correct damage per click is used when reloading the game
      updateStats(buildStatsSnapshot());

      newState = state;
      emit(GUIActions.INIT_GAME);
      emit(GUIActions.START_LEVEL, state);

      // Intro flag must reflect whether the player actually saw/skimmed the intro (or we skipped dialogs).
      if (CONFIG.game.skipDialogs) {
        useGameStatusStore.getState().markIntroPlayed();
      } else if (!useGameStatusStore.getState().playedIntro) {
        openDialog('intro', language, true);
        useGameStatusStore.getState().markIntroPlayed();
      }

      break;
    case GUIActions.CHANGE_LEVEL:
      const { levelName } = action.payload;
      useGameStatsStore.getState().setActiveLevelId(sceneNameToGameLevelId(levelName));
      emit(GameEvents.CHANGE_SCENE, { levelName });

      setTimeout(() => {
        emit(GUIActions.START_LEVEL, state);
      }, 200);

      if (levelName === 'dummy') {
        useGameStatusStore.getState().sync({ dragonName: 'dummy', metBigDragon: false });
        useDragonHealthStore.getState().setHealth(CONFIG.game.initialDragonHealth.dummy);
      }
      newState = state;

      break;
    case GUIActions.WIN_GAME: {
      const { unlockAchievement } = AchievementsHandler();
      unlockAchievement('BEAT_TUTORIAL', 500);
      const thievesGeneratorIds = ['thief', 'guild', 'congress'] as const;
      const generatorsSnapshot = useGeneratorsStore.getState().generators;
      const onlyThievesOwned = (Object.keys(generatorsSnapshot) as (keyof typeof generatorsSnapshot)[]).every(
        (id) =>
          generatorsSnapshot['thief'].owned > 0 &&
          (generatorsSnapshot[id].owned === 0 ||
            thievesGeneratorIds.includes(id as (typeof thievesGeneratorIds)[number])),
      );
      if (onlyThievesOwned) {
        unlockAchievement('ONLY_THIEVES_VICTORY', 1500);
      }
      useGameStatusStore.getState().markWon();
      useGameStatusStore.getState().markLevelComplete('tutorial');
      newState = state;
      emit(GUIActions.WIN_GAME, state);
      break;
    }
    case GUIActions.DUMMY_KILLED:
      const { unlockAchievement } = AchievementsHandler();
      unlockAchievement('BEAT_DUMMY', 500);

      actions.setGlobalVariables({ killedDummy: true });
      useGameStatusStore.getState().markDummyKilled();
      useGameStatusStore.getState().markLevelComplete('dummy');
      newState = state;
      emit(GUIActions.DUMMY_KILLED, state);
      break;
    case GUIActions.SHOWN_FIRST_MANA_OUT_DIALOG:
      useGameStatusStore.getState().markShownFirstManaOutDialog();
      newState = state;
      break;
    case GUIActions.OPEN_DUNGEON_CRAWLER: {
      // Dungeon lives in Zustand only; do not use openDungeon() here — it resets currentLevel to 0.
      const dungeonSnapshot = useDungeonCrawlerStore.getState();
      if (dungeonSnapshot.keys <= 0 || dungeonSnapshot.remainingCooldown > 0) {
        return gameState;
      }
      if (!dungeonSnapshot.consumeKey()) {
        return gameState;
      }
      useDungeonCrawlerStore.getState().sync({
        isOpen: true,
        goldWhenEntered: useCurrencyStore.getState().gold,
      });
      newState = state;
      break;
    }
    case GUIActions.CLOSE_DUNGEON_CRAWLER: {
      useDungeonCrawlerStore.getState().closeDungeon(action.payload?.completed);
      newState = state;
      break;
    }
    case GUIActions.ADD_DUNGEON_REWARDS:
      useCurrencyStore.getState().addGold(action.payload.gold);
      {
        const dungeonAfterGold = useDungeonCrawlerStore.getState();
        useDungeonCrawlerStore.getState().sync({
          // Preserve explicit 0 (?? only falls back on null/undefined).
          currentLevel: action.payload.currentLevel ?? dungeonAfterGold.currentLevel,
          keys: dungeonAfterGold.keys + action.payload.keys,
        });
      }
      newState = state;
      break;
    case GUIActions.CONSUME_DUNGEON_KEY:
      useDungeonCrawlerStore.getState().consumeKey();
      newState = state;
      break;
    case GUIActions.TOGGLE_BUY_MAX:
      useGameStatusStore.getState().toggleBuyMax();
      const buyMaxAfterToggle = useGameStatusStore.getState().buyMax;

      playSFXNow(MenuEvents.CHANGE_VALUE);

      emit(GUIActions.TOGGLE_BUY_MAX, { buyMax: buyMaxAfterToggle });
      newState = state;
      break;
    case GUIActions.TOGGLE_UI:
      useGameStatusStore.getState().toggleShowUI();
      const showUIAfterToggle = useGameStatusStore.getState().showUI;

      playSFXNow(MenuEvents.CHANGE_VALUE);

      emit(GUIActions.TOGGLE_UI, { showUI: showUIAfterToggle });
      newState = state;
      break;
    case GUIActions.UNLOCK_ALL:
      useGeneratorsStore.getState().unlockAllGenerators();
      useUpgradesStore.getState().unlockAllUpgrades();
      newState = state;
      break;
    case GUIActions.CHEATS:
      const property = action.payload.property;

      // If passing newValue, use that.
      // If not, use increase + current value.
      // If not passing increase, use 1000.
      const currencySnapshot = useCurrencyStore.getState();
      const numericCurrencyKeys = ['gold', 'wood', 'ore', 'food', 'mana', 'goldLastTick'] as const;
      const dragonNumericKeys = ['dragonHealth', 'remainingPoison', 'damageLastTick'] as const;
      const dragonSnapshot = useDragonHealthStore.getState();
      const gameStatusSnapshot = useGameStatusStore.getState();
      const cheatBaseValue = (numericCurrencyKeys as readonly string[]).includes(property)
        ? (currencySnapshot as Record<string, unknown>)[property]
        : (dragonNumericKeys as readonly string[]).includes(property)
          ? (dragonSnapshot as Record<string, unknown>)[property]
          : property === 'saveVersion'
            ? gameStatusSnapshot.saveVersion
            : property === 'tickLength'
              ? gameStatusSnapshot.tickLength
              : undefined;
      let value =
        action.payload.newValue ||
        action.payload.increase + (typeof cheatBaseValue === 'number' ? cheatBaseValue : 0) ||
        1000;

      if (property === 'tickLength') {
        switch (gameStatusSnapshot.tickLength) {
          case 1000:
            value = 500;
            break;
          case 500:
            value = 100;
            break;
          case 100:
            value = 10;
            break;
          case 10:
            value = 1000;
            break;
          default:
            value = 1000;
            break;
        }
      }

      if ((numericCurrencyKeys as readonly string[]).includes(property) && typeof value === 'number') {
        useCurrencyStore.getState().sync({ [property]: value });
        newState = state;
      } else if ((dragonNumericKeys as readonly string[]).includes(property) && typeof value === 'number') {
        useDragonHealthStore.getState().sync({ [property]: value } as {
          dragonHealth?: number;
          remainingPoison?: number;
          damageLastTick?: number;
        });
        newState = state;
      } else if (property === 'tickLength' && typeof value === 'number') {
        useGameStatusStore.getState().sync({ tickLength: value });
        newState = state;
      } else if (property === 'saveVersion' && typeof value === 'string') {
        useGameStatusStore.getState().sync({ saveVersion: value });
        newState = state;
      } else if (property === 'inspiration' && typeof value === 'object' && value !== null) {
        useCurrencyStore.getState().setInspiration(value as Partial<(typeof currencySnapshot)['inspiration']>);
        newState = state;
      } else if (property === 'upgrades' && typeof value === 'object' && value !== null) {
        // Cheat / dev: replace entire upgrades map (same shape as `EntityUpgradesList`).
        useUpgradesStore.getState().setUpgrades(value as EntityUpgradesList);
        newState = state;
      } else {
        newState = state;
      }
      break;
    default:
      newState = state;
  }

  // Coup de grace logic
  const gameStatusForCoup = useGameStatusStore.getState();
  const dragonHealthForCoup = useDragonHealthStore.getState().dragonHealth;
  if (gameStatusForCoup.dragonName !== 'dummy' && dragonHealthForCoup <= 1 && !gameStatusForCoup.playedCoupDeGrace) {
    useDragonHealthStore.getState().setHealth(1);
    useGameStatusStore.getState().markCoupDeGracePlayed();

    emit(GameplayEvents.WAIT_FOR_LAST_HIT);
    saveState(newState);
  }

  // Only update stats when relevant actions occur that affect damage/gold calculations
  const statsRelevantActions = [
    GUIActions.BUY_GENERATOR,
    GUIActions.ADD_GENERATORS,
    GUIActions.REMOVE_GENERATORS,
    GUIActions.ACTIVATE_INSPIRATION,
    GUIActions.GENERATE_GENERATORS,
    UpgradeActions.BUY_UPGRADE,
  ];
  if (statsRelevantActions.includes(action.type)) {
    updateStats(buildStatsSnapshot());
  }

  const currentLevelName = gameStatusForCoup.dragonName;
  if (!useGameStatusStore.getState().won) {
    // saveState(newState);
  } else if (currentLevelName === 'dummy') {
    // saveState(newState);
  }

  // Set global state variable (gold lives only in Zustand).
  const currentGold = useCurrencyStore.getState().gold;
  actions.setGlobalVariables({ gold: currentGold });
  setStatFloat('gold_collected', currentGold);

  return newState;
};

/** Dispatched actions for the GUI reducer (split from state so dispatch-only subscribers skip state updates). */
export type GameReducerAction = { type: GUIActions | UpgradeActions; payload?: unknown };

export const GameDispatchContext = createContext<Dispatch<GameReducerAction> | undefined>(undefined);

export const GameStateProvider = ({ children }: { children: ReactNode }) => {
  // State is always `{}`; we keep `useReducer` for a stable `dispatch` and side-effect orchestration only.
  const [, dispatch] = useReducer(reducer, EMPTY_GUI_GAME_STATE);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      const data = await ensureGameStateBootstrapped();
      if (!cancelled) {
        dispatch({ type: GUIActions.INIT_GAME, payload: data });
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, []);

  return <GameDispatchContext.Provider value={dispatch}>{children}</GameDispatchContext.Provider>;
};

export function useGameDispatch(): Dispatch<GameReducerAction> {
  const dispatch = useContext(GameDispatchContext);

  if (dispatch === undefined) {
    throw new Error('useGameDispatch must be used within GameStateProvider');
  }

  return dispatch;
}

export function useGeneratorsByLevel(level: number, type: UnitType): Generator[] {
  const dispatch = useContext(GameDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useGeneratorsByLevel must be used within GameStateProvider');
  }

  const generators = useGeneratorsStore((store) => store.generators);

  // Memoize to prevent creating new array references every render
  if (level === 1) {
    if (type === UnitType.TROOPS) {
      return useMemo(() => [generators.warrior, generators.wizard, generators.elf], [generators]);
    } else {
      return useMemo(() => [generators.thief, generators.bard, generators.cleric], [generators]);
    }
  } else if (level === 2) {
    if (type === UnitType.TROOPS) {
      return useMemo(() => [generators.garrison, generators.academy, generators.outpost], [generators]);
    } else {
      return useMemo(() => [generators.guild, generators.troupe, generators.seminary], [generators]);
    }
  } else if (level === 3) {
    if (type === UnitType.TROOPS) {
      return useMemo(() => [generators.council, generators.nexus, generators.forest], [generators]);
    } else {
      return useMemo(() => [generators.congress, generators.theater, generators.college], [generators]);
    }
  }
  return [];
}
